import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculateBenefitPremiumWithDetails } from '@/components/utils/benefitPricing';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import PayStubPreview from '@/components/enrollment/PayStubPreview';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  ArrowLeft,
  ArrowRight,
  Play,
  Heart,
  CheckCircle2,
  HelpCircle,
  FileText,
  DollarSign,
  Shield,
  MessageCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function BenefitDetail() {
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [existingEnrollment, setExistingEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [calculatedPremium, setCalculatedPremium] = useState(null);
  const [calculationDetails, setCalculationDetails] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('id');
    
    if (!productId) {
      setLoading(false);
      return;
    }

    try {
      const user = await api.auth.me();
      
      const [productsData, employees] = await Promise.all([
        api.entities.BenefitProduct.filter({ id: productId }),
        api.entities.Employee.filter({ user_email: user.email })
      ]);

      if (productsData.length > 0) {
        const prod = productsData[0];
        
        // Parse JSON fields
        let features = prod.features || [];
        if (typeof features === 'string') {
          try {
            features = JSON.parse(features);
          } catch (e) {
            console.error('Failed to parse features:', e);
            features = [];
          }
        }
        
        let faq = prod.faq || [];
        if (typeof faq === 'string') {
          try {
            faq = JSON.parse(faq);
          } catch (e) {
            console.error('Failed to parse faq:', e);
            faq = [];
          }
        }
        
        setProduct({
          ...prod,
          features,
          faq
        });
        
        const categories = await api.entities.BenefitCategory.filter({ id: prod.category_id });
        if (categories.length > 0) setCategory(categories[0]);
      }

      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        const [enrollments, family] = await Promise.all([
          api.entities.Enrollment.filter({ 
            employee_id: emp.id,
            product_id: productId,
            status: 'active'
          }),
          api.entities.FamilyMember.filter({ 
            employee_id: emp.id,
            is_active: true
          })
        ]);
        
        setFamilyMembers(family);
        
        if (enrollments.length > 0) {
          setExistingEnrollment(enrollments[0]);
        }
        
        // Calculate personalized premium using rating tables
        if (productsData.length > 0) {
          const { premium, details } = await calculateBenefitPremiumWithDetails(
            productsData[0],
            emp,
            family,
            'employee_only'
          );
          setCalculatedPremium(premium);
          setCalculationDetails(details);
          console.log('Calculation details:', details);

          // Calculate tax savings for this benefit
          try {
            const annualPremium = premium * 12;
            const taxAnalysis = await calculateEmployeeTaxes(emp, annualPremium);
            emp.estimatedTaxSavings = taxAnalysis.savings.total;
          } catch (err) {
            console.warn('Tax calculation unavailable:', err);
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-16">
        <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-700 mb-2">Benefit Not Found</h3>
        <Link to={createPageUrl('BenefitsCatalog')}>
          <Button>Back to Catalog</Button>
        </Link>
      </div>
    );
  }

  const hasMissingPricingData = employee && (!employee.date_of_birth || !employee.gender);

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link to={createPageUrl('BenefitsCatalog')}>
        <Button variant="ghost" className="text-slate-600">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Catalog
        </Button>
      </Link>

      {/* Missing Data Warning */}
      {hasMissingPricingData && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
          <CardContent className="p-4 flex items-start gap-4">
            <HelpCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 mb-1">Price Calculation Unavailable</p>
              <p className="text-sm text-amber-800 mb-3">
                Your profile is missing required information (date of birth and gender). 
                Please complete your profile to see accurate personalized pricing.
              </p>
              <Link to={createPageUrl('EmployeeProfile')}>
                <Button size="sm" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  Complete My Profile
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid lg:grid-cols-2 gap-8"
      >
        {/* Image/Video */}
        <div className="relative rounded-2xl overflow-hidden">
          {showVideo && product.video_url ? (
            <div className="aspect-video">
              <iframe
                src={product.video_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="relative aspect-video">
              <img
                src={product.image_url || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.video_url && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 group"
                >
                  <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Play className="w-8 h-8 text-emerald-600 ml-1" />
                  </div>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              {category?.name || 'General'}
            </Badge>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{product.name}</h1>
            <p className="text-lg text-slate-600">{product.short_description}</p>
          </div>

          {/* Price Card */}
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-end justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm text-slate-500">Your Monthly Premium</p>
                    <div className="group relative">
                      <HelpCircle className="w-4 h-4 text-slate-400 cursor-help hover:text-slate-600" />
                      {calculationDetails && (
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-80">
                          <div className="bg-slate-900 text-white text-xs rounded-lg p-4 shadow-xl">
                            <p className="font-semibold mb-2">Calculation Breakdown</p>
                            <div className="space-y-1">
                              <p className="text-slate-300">Method: {calculationDetails.method}</p>
                              {calculationDetails.ratingTableName && (
                                <p className="text-slate-300">Table: {calculationDetails.ratingTableName}</p>
                              )}
                              {calculationDetails.breakdown.map((line, i) => (
                                <p key={`calc-${i}`} className="text-slate-300">• {line}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-slate-900">
                    ${(calculatedPremium ?? parseFloat(product?.base_premium || 0)).toFixed(2)}
                    <span className="text-base font-normal text-slate-500">/month</span>
                  </p>
                  {product.pricing_method !== 'flat_rate' && (
                            <p className="text-xs text-slate-500 mt-1">
                              Personalized rate based on your demographics
                            </p>
                          )}
                          {employee?.estimatedTaxSavings > 0 && (
                            <p className="text-xs text-emerald-600 mt-2 font-medium">
                              ðŸ’° Est. tax savings: ${employee.estimatedTaxSavings.toFixed(2)}/year
                            </p>
                          )}
                </div>
                {product.coverage_amount && (
                  <div className="text-right">
                    <p className="text-sm text-slate-500 mb-1">Coverage Amount</p>
                    <p className="text-2xl font-semibold text-emerald-600">
                      ${product.coverage_amount?.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          {existingEnrollment ? (
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-800">You're Enrolled!</p>
                  <p className="text-sm text-emerald-600">This benefit is active on your account.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 shadow-xl sticky top-20 z-10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Monthly Premium</p>
                    <p className="text-3xl font-bold text-emerald-700">${(calculatedPremium ?? parseFloat(product.base_premium || 0)).toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Estimated Annual Tax Savings</p>
                    <p className="text-xl font-bold text-teal-600">${((calculatedPremium ?? parseFloat(product.base_premium || 0)) * 0.25 * 12).toFixed(0)}</p>
                  </div>
                </div>
                <Link to={createPageUrl(`Enrollment?productId=${product.id}`)} className="block">
                  <Button size="lg" className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg text-lg py-6 transition-all hover:scale-105">
                    <Heart className="w-6 h-6 mr-2" />
                    Start Enrollment Wizard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <p className="text-xs text-center text-slate-600 mt-3 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Section 125 pre-tax savings • No medical exam
                </p>
              </CardContent>
            </Card>
          )}

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">Key Features</h3>
              <div className="grid grid-cols-2 gap-3">
                {product.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Pay Stub Preview */}
      {employee && calculatedPremium && calculatedPremium > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <PayStubPreview employee={employee} monthlyPremium={calculatedPremium} />
        </motion.div>
      )}

      {/* Details Tabs */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <Tabs defaultValue="description" className="w-full">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="description" className="data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Description
            </TabsTrigger>
            <TabsTrigger value="faq" className="data-[state=active]:bg-white">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </TabsTrigger>
            <TabsTrigger value="coverage" className="data-[state=active]:bg-white">
              <Shield className="w-4 h-4 mr-2" />
              Coverage Details
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="description" className="mt-6">
            <Card>
              <CardContent className="p-6 prose max-w-none">
                <p className="text-slate-600 leading-relaxed">
                  {product.full_description || product.short_description || 
                    'This comprehensive benefit package provides excellent coverage designed to protect you and your family. Contact our benefits team for more detailed information about coverage options and enrollment.'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="faq" className="mt-6">
            <Card>
              <CardContent className="p-6">
                {product.faq && product.faq.length > 0 ? (
                  <Accordion type="single" collapsible className="w-full">
                    {product.faq.map((item, idx) => (
                      <AccordionItem key={idx} value={`faq-${idx}`}>
                        <AccordionTrigger className="text-left">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <div className="text-center py-8">
                    <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No FAQs available for this benefit.</p>
                    <Link to={createPageUrl('Help')}>
                      <Button variant="link" className="mt-2">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Ask Victoria AI
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="coverage" className="mt-6">
            <Card>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Coverage Amount</p>
                        <p className="font-semibold">${product.coverage_amount?.toLocaleString() || 'Contact for quote'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Policy Number</p>
                        <p className="font-semibold">{product.policy_number || 'Assigned upon enrollment'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Important Notes</h4>
                    <ul className="text-sm text-slate-600 space-y-2">
                      <li>â€¢ Coverage begins on the first of the month following enrollment</li>
                      <li>â€¢ Premium may vary based on age and location</li>
                      <li>â€¢ Pre-existing condition limitations may apply</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}